import { z } from 'zod';
import { ModuleKeySchema, type LiveTurnState } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';

/**
 * Reattaching to a chat turn that outlived its connection (AI-520). The
 * backend keeps running a turn after a refresh or a dropped stream and serves
 * its live state through `subscribeTurn`; this module turns those snapshots
 * into the agent-state shapes the chat UI already renders.
 */

const STORAGE_KEY = 'nami.liveThread';

const RememberedThreadSchema = z.object({
  threadId: z.uuid(),
  moduleKey: ModuleKeySchema,
  /**
   * The page entity (e.g. `workbook:<id>`) each submission this tab made was
   * sent from, by request id, so a refresh elsewhere does not run its
   * page-bound tool calls. Absent for a thread opened from history.
   */
  scopes: z.record(z.string(), z.string()).catch({}),
});
type RememberedThread = z.infer<typeof RememberedThreadSchema>;

/** Tab-scoped, so a refresh can find the thread whose turn may still be running. */
export function rememberThread(thread: RememberedThread | null): void {
  try {
    if (thread) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(thread));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, quota); resuming is best-effort.
  }
}

export function rememberedThread(): RememberedThread | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = RememberedThreadSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    // Unreadable entry: start fresh.
    return null;
  }
}

/** Shaped like agent-state's `ToolCall`, so the panels render a reattached turn as-is. */
export function liveToolCalls(turn: LiveTurnState) {
  return turn.toolCalls.map((call) => ({
    toolCallId: call.toolCallId,
    toolName: call.toolName,
    userFacingName: call.userFacingName,
    isWrite: call.isWrite,
    createdAt: call.startedAt,
    args: call.args,
    result: call.result,
    status:
      call.status === 'running' ? ('pending' as const) : ('complete' as const),
  }));
}

/**
 * Stop for a detached turn; closing the stream no longer cancels it.
 * Resolves (never rejects) once the server has flagged the stop, which
 * frees the turn's slot under the per-user cap (AI-527).
 */
export function stopTurnOnServer(
  threadId: string,
  runId?: string,
): Promise<void> {
  return rpcClient.ai.chatAgent
    .stopTurn({ threadId, runId })
    .then(() => undefined)
    .catch((err: unknown) => {
      console.warn('stopTurn failed', err);
    });
}

/**
 * How long a send waits for a Stop just sent. A stalled stopTurn must not
 * block sending; the server's turn cap still holds without the wait.
 */
export const STOP_WAIT_MS = 3_000;

/**
 * How long a message queued behind a send waits for the server to admit that
 * send (its Stop wait plus the request), so the two arrive in order.
 */
export const ADMISSION_WAIT_MS = 10_000;

/** Waits for `promise`, but no longer than `ms`. */
export function waitAtMost(promise: Promise<void>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export interface FollowLiveTurnHandlers {
  /** Each snapshot while the turn runs; `first` marks the first one seen. */
  onRunning: (turn: LiveTurnState, first: boolean) => void | Promise<void>;
  /** The turn ended after this follower saw it running, or with `settleUnseen`. */
  onSettled: (turn: LiveTurnState) => void | Promise<void>;
  /**
   * Also settle a turn that already ended before the first frame. Set after
   * a dropped stream: its partial answer is on screen and must be replaced.
   */
  settleUnseen?: boolean;
}

/**
 * Follows the thread's turn until it ends. Resolves immediately when no turn
 * is running, so callers can attach unconditionally.
 */
export async function followLiveTurn(
  threadId: string,
  signal: AbortSignal,
  handlers: FollowLiveTurnHandlers,
): Promise<void> {
  let sawRunning = false;
  const frames = await rpcClient.ai.chatAgent.subscribeTurn(
    { threadId },
    { signal },
  );
  for await (const frame of frames) {
    if (signal.aborted) return;
    const turn = frame.turn;
    if (!turn) return;
    if (turn.status === 'running') {
      await handlers.onRunning(turn, !sawRunning);
      sawRunning = true;
    } else {
      if (sawRunning || handlers.settleUnseen) await handlers.onSettled(turn);
      return;
    }
  }
}

/**
 * Stops a run and waits (at most `timeoutMs`) until the thread's turn has
 * ended, so a message sent next starts its own turn instead of queueing
 * behind the stopped one (AI-518). For callers whose next message replaces
 * the last, like the wizard.
 */
export async function stopAndAwaitTurn(
  threadId: string,
  runId: string,
  timeoutMs = 5_000,
): Promise<void> {
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort(), timeoutMs);
  try {
    await rpcClient.ai.chatAgent.stopTurn({ threadId, runId });
    await followLiveTurn(threadId, deadline.signal, {
      onRunning: () => {},
      onSettled: () => {},
    });
  } catch (err) {
    if (!deadline.signal.aborted) console.warn('stopAndAwaitTurn failed', err);
  } finally {
    clearTimeout(timer);
  }
}

const CLAIMS_KEY = 'nami.claimedToolCalls';

/** Tool calls this tab already started, kept across a reload (tab-scoped). */
export function persistedClaims(): string[] {
  try {
    const parsed = z
      .array(z.string())
      .safeParse(JSON.parse(sessionStorage.getItem(CLAIMS_KEY) ?? '[]'));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

export function persistClaim(toolCallId: string): void {
  try {
    const ids = persistedClaims();
    if (ids.includes(toolCallId)) return;
    sessionStorage.setItem(
      CLAIMS_KEY,
      JSON.stringify([...ids, toolCallId].slice(-200)),
    );
  } catch {
    // Storage can be unavailable; the in-memory claim still holds this visit.
  }
}

/** Forgets a persisted claim, for a call that was cancelled before it ran. */
export function releaseClaim(toolCallId: string): void {
  try {
    const ids = persistedClaims();
    if (!ids.includes(toolCallId)) return;
    sessionStorage.setItem(
      CLAIMS_KEY,
      JSON.stringify(ids.filter((id) => id !== toolCallId)),
    );
  } catch {
    // Storage can be unavailable; nothing persisted to release.
  }
}
